import { Component, computed, OnInit, signal, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { RideStatusType } from '../../../../shared/enums/rideStatusType';
import { RequestBuilderService } from '../../../../shared/services/request-builder.service';
import { TooltipOptions } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-ride-mass-cancel',
  standalone: true,
  imports: [ReactiveFormsModule, ButtonComponent, TextareaModule, AutoCompleteModule, SelectModule, DialogModule, TooltipModule],
  templateUrl: './ride-mass-cancel.component.html',
  styleUrl: './ride-mass-cancel.component.scss',
})
export class RideMassCancelComponent implements OnInit {
  ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  private readonly requestBuilder = inject(RequestBuilderService);
  private readonly fb = inject(FormBuilder);

  statusOptions = signal<{ name: string; id: number }[]>([
    { name: `Late Cancel`, id: RideStatusType.CanceledLate },
    { name: `Early Cancel`, id: RideStatusType.CanceledInAdvance },
  ]);

  reasons = signal<{ name: string; id: number }[]>([]);
  clients = signal<{ name: string; id: number }[]>([]);
  places = signal<{ name: string; id: number }[]>([]);

  form!: FormGroup;

  confirmationDialogVisible = false;
  rideCargoIds: number[] = [];
  rideCount: number = 0;

  loading = false;
  suggestions: string[] = [];
  filteredSuggestions: string[] = [];

  tooltipOptions: TooltipOptions = {
    showDelay: 500,
    tooltipEvent: 'hover',
    tooltipPosition: 'bottom',
  };

  isSingleRideCancellation = computed(() => this.config.data?.isSingleRideCancellation ?? false);

  // Signal to track form value changes
  private readonly formValueSignal = signal<any>(null);

  isSubmitDisabled = computed(() => {
    // Access the signal to make this computed reactive
    this.formValueSignal();

    if (this.loading || !this.form) return true;

    const reasonValue = this.form.get('reason')?.value;
    const otherReasonValue = this.form.get('otherReason')?.value;

    // Check if reason is selected and has valid name
    if (!reasonValue?.name || reasonValue.name.trim().length === 0) {
      return true;
    }

    // If reason is 'Other', check if otherReason is filled
    if (reasonValue.name === 'Other' && (!otherReasonValue || otherReasonValue.trim().length === 0)) {
      return true;
    }

    // For non-single ride cancellation, at least one of client or place must be selected
    if (!this.isSingleRideCancellation()) {
      const clientValue = this.form.get('client')?.value;
      const placeValue = this.form.get('place')?.value;

      if (!clientValue && !placeValue) {
        return true;
      }
    }

    return !this.form.valid; // false if all validations pass and the form is valid
  });

  constructor() {
    if (!this.config.data.isPendingRides) {
      this.statusOptions.update(options => [{ name: `Automatic`, id: RideStatusType.Automatic }, ...options]);
    }
  }

  ngOnInit() {
    console.log(this.config);

    this.initForm();
    this.getClients();
    this.getPlaces();
    this.getReasons();
    this.filteredSuggestions = [...this.suggestions];
  }

  initForm() {
    this.form = this.fb.group({
      status: [this.config.data.isPendingRides ? null : RideStatusType.Automatic, Validators.required],
      client: [null],
      place: [null],
      reason: [null, Validators.required],
      otherReason: [''],
    });

    // Watch for reason changes to toggle otherReason validator
    this.form.get('reason')?.valueChanges.subscribe(reason => {
      const otherReasonControl = this.form.get('otherReason');
      if (reason?.name === 'Other') {
        otherReasonControl?.setValidators([Validators.required]);
      } else {
        otherReasonControl?.clearValidators();
        otherReasonControl?.setValue('');
      }
      otherReasonControl?.updateValueAndValidity();
    });

    // Update signal whenever form values change to trigger computed signal
    this.form.valueChanges.subscribe(() => {
      this.formValueSignal.set(this.form.value);
    });

    // Trigger initial value
    this.formValueSignal.set(this.form.value);
  }

  getClients() {
    this.requestBuilder.get(`filters/clients`).subscribe(res => {
      this.clients.set(
        res.data.map((x: any) => {
          return { name: x.name, id: x.id };
        }),
      );
    });
  }
  getReasons() {
    this.requestBuilder.get(`rides/cancellation-reasons`).subscribe(res => {
      this.reasons.set(
        res.data.map((x: any) => {
          return { name: x.name, id: x.id };
        }),
      );
    });
  }
  getPlaces() {
    this.requestBuilder.get(`filters/places`).subscribe(res => {
      this.places.set(
        res.data.map((x: any) => {
          return { name: x.name, id: x.id };
        }),
      );
    });
  }

  closeConfirmDialog() {
    this.confirmationDialogVisible = false;
  }

  openConfirmDialog() {
    if (this.form.invalid) return;

    this.rideCargoIds = [];
    const placeId = this.form.get('place')?.value?.id ?? 0;
    const clientId = this.form.get('client')?.value?.id ?? 0;

    if (this.isSingleRideCancellation()) {
      this.confirmationDialogVisible = true;
      return;
    }

    this.requestBuilder.get(`rides/mass-cancellation-checker/${placeId}/${clientId}`).subscribe({
      next: res => {
        this.rideCargoIds = res.data.rideCargoIds;
        this.confirmationDialogVisible = true;
        this.rideCount = res.data.rideCount;
      },
      error: error => {},
    });
  }

  confirmAction() {
    if (this.form.invalid) return;

    this.loading = true;

    const reasonValue = this.form.get('reason')?.value;
    const otherReasonValue = this.form.get('otherReason')?.value;
    const cancellationStatusId = this.form.get('status')?.value;

    if (this.isSingleRideCancellation()) {
      this.requestBuilder.post(`rides/${this.config.data.ride.uuid}/cancel-ride/${cancellationStatusId}/${reasonValue.id}?internalNote=${otherReasonValue}`, {}).subscribe({
        next: () => {
          this.loading = false;
          this.ref.close({ confirmed: true });
        },
        error: error => {
          this.loading = false;
        },
      });
    } else {
      const body: any = {
        rideCargoIds: this.rideCargoIds,
        placeId: this.form.get('place')?.value?.id ?? 0,
        clientId: this.form.get('client')?.value?.id ?? 0,
        cancellationStatusId,
        cancellationReason: reasonValue?.id,
      };
      if (reasonValue?.name === 'Other') {
        body.internalNote = otherReasonValue;
      }

      this.requestBuilder.post(`rides/mass-cancellation-operation`, body).subscribe({
        next: () => {
          this.loading = false;
          this.ref.close({ confirmed: true });
        },
        error: error => {
          this.loading = false;
        },
      });
    }
  }
}
